import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, Bell, Check, AlertCircle, Loader } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import Logo from '../assets/images/arka-saydam-logo.png';

const PartnerAccept = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [accepting, setAccepting] = useState(false);
    const [inviteData, setInviteData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        validateToken();
    }, [token]);

    const validateToken = async () => {
        try {
            setLoading(true);
            // TODO: Backend endpoint to validate and get invite details
            const response = await axios.get(`/api/profile/partner/invite/${token}`);
            setInviteData(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Geçersiz davet linki');
        } finally {
            setLoading(false);
        }
    };

    const handleAccept = async () => {
        const tokenInStorage = localStorage.getItem('authToken');

        if (!tokenInStorage) {
            // Redirect to registration flow
            toast.success('Kaydınızı tamamlayarak şefliğe başlayın!');
            navigate(`/partner/register/${token}`);
            return;
        }

        try {
            setAccepting(true);
            await axios.post(`/api/profile/partner/accept/${token}`);
            toast.success('Partner bağlantısı başarıyla kuruldu!');
            setTimeout(() => {
                navigate('/dashboard');
            }, 2000);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Davet kabul edilemedi');
        } finally {
            setAccepting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
                <div className="text-center">
                    <Loader className="animate-spin text-blue-500 mx-auto mb-4" size={48} />
                    <p className="text-gray-600">Davet kontrol ediliyor...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="text-red-500" size={32} />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Davet Geçersiz</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                    >
                        Ana Sayfaya Dön
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl max-w-md w-full overflow-hidden border border-slate-100 dark:border-slate-800 animate-in fade-in zoom-in duration-500">
                {/* Visual Header Banner */}
                <div className="bg-gradient-to-b from-[#6366f1] to-[#8b5cf6] p-10 pb-16 text-center relative overflow-hidden">
                    {/* Decorative Circles */}
                    <div className="absolute top-4 left-4 w-2 h-2 rounded-full bg-white/20"></div>
                    <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-white/20"></div>
                    <div className="absolute bottom-12 left-1/4 w-2 h-2 rounded-full bg-white/10"></div>
                    <div className="absolute bottom-8 right-1/4 w-2 h-2 rounded-full bg-white/10"></div>

                    <div className="relative z-10">
                        <div className="w-24 h-24 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-6 border border-white/30 shadow-xl">
                            <span className="text-5xl">🤝</span>
                        </div>
                        <h1 className="text-3xl font-black text-white mb-2 tracking-tight">Partner Daveti!</h1>
                        <p className="text-white/80 font-medium">Yeni Bir Alışveriş Ortağı</p>
                    </div>

                    {/* Bottom Arc Decoration */}
                    <div className="absolute bottom-0 left-0 right-0 h-8 bg-white dark:bg-slate-900 rounded-t-[2.5rem]"></div>
                </div>

                <div className="px-8 pb-10">
                    {/* Welcome Text */}
                    <div className="text-center mb-8">
                        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                            Şefim; <span className="font-black text-[#6366f1]">{inviteData?.inviter?.name || 'Özgür'}</span> şef tarafından size ortak alışveriş listesi yollandı daveti kabul ederek ilgili listeyi birlikte tamamlayabilirsiniz.
                        </p>
                    </div>

                    {/* Feature Cards */}
                    <div className="space-y-4 mb-8">
                        <div className="flex items-center gap-4 p-4 bg-indigo-50/50 dark:bg-indigo-900/10 rounded-3xl border border-indigo-100/50 dark:border-indigo-500/10 transition-all hover:scale-[1.02]">
                            <div className="w-12 h-12 bg-[#6366f1] rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-indigo-200 dark:shadow-none">
                                <ShoppingCart className="text-white" size={24} />
                            </div>
                            <div>
                                <h3 className="font-bold text-slate-800 dark:text-slate-200 leading-tight">Ortak Market Listeleri</h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400">Listeleri anlık paylaş ve beraber düzenle.</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-4 p-4 bg-purple-50/50 dark:bg-purple-900/10 rounded-3xl border border-purple-100/50 dark:border-purple-500/10 transition-all hover:scale-[1.02]">
                            <div className="w-12 h-12 bg-[#8b5cf6] rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-purple-200 dark:shadow-none">
                                <Bell className="text-white" size={24} />
                            </div>
                            <div>
                                <h3 className="font-bold text-slate-800 dark:text-slate-200 leading-tight">Anlık Bildirimler</h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400">Partner bir şey eklediğinde bildirim al.</p>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-4 mb-8">
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="flex-1 py-4 px-6 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all active:scale-95"
                        >
                            Reddet
                        </button>
                        <button
                            onClick={handleAccept}
                            disabled={accepting}
                            className="flex-[1.5] py-4 px-6 bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] text-white font-bold rounded-2xl shadow-xl shadow-indigo-200 dark:shadow-none hover:opacity-90 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {accepting ? <Loader className="animate-spin" size={20} /> : 'Daveti Kabul Et'}
                        </button>
                    </div>

                    {/* Integrated Branding */}
                    <div className="pt-6 border-t border-slate-100 dark:border-slate-800 flex flex-col items-center gap-1.5 opacity-60">
                        <div className="flex items-center gap-2">
                            <img src={Logo} alt="Logo" className="w-6 h-6 object-contain" />
                            <span className="text-slate-900 dark:text-white text-sm font-black tracking-tighter">Bana Bir Tarif Ver</span>
                        </div>
                        <p className="text-[8px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">Akıllı Mutfak Asistanı</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PartnerAccept;
